/**
 * SEMANTIC SEARCH SERVICE
 * 
 * Provides semantic understanding layer between user queries and project data.
 * Uses OpenAI embeddings for semantic similarity and construction domain knowledge.
 * 
 * Key Features:
 * 1. Text embeddings for semantic similarity
 * 2. Construction terminology ontology
 * 3. Query expansion with synonyms
 * 4. Location hierarchy understanding
 */

import axios from 'axios';

class SemanticSearchService {
  
  // Cache for embeddings to avoid redundant API calls
  static embeddingCache = new Map();
  
  // Construction domain ontology
  static CONSTRUCTION_ONTOLOGY = {
    // Building height classifications
    building_height: {
      'super tall': { min_height: 984, synonyms: ['supertall', 'ultra high-rise', 'mega tall', 'skyscraper'] },
      'tall': { min_height: 600, max_height: 983, synonyms: ['very tall', 'tall tower'] },
      'high-rise': { min_height: 75, max_height: 599, synonyms: ['high rise', 'tower', 'tall building'] },
      'mid-rise': { min_height: 35, max_height: 74, synonyms: ['midrise', 'medium rise', 'mid rise'] },
      'low-rise': { max_height: 34, synonyms: ['low rise', 'lowrise', 'garden style'] }
    },
    
    // Location hierarchy - NYC focus
    locations: {
      'new york city': {
        aliases: ['nyc', 'new york', 'ny', 'new york, ny'],
        children: {
          'manhattan': {
            aliases: ['new york county', 'manhattan borough'],
            neighborhoods: {
              'tribeca': { aliases: ['triangle below canal', 'tri-be-ca'], zipcodes: ['10007', '10013'] },
              'soho': { aliases: ['south of houston', 'so-ho'], zipcodes: ['10012', '10013'] },
              'nomad': { aliases: ['north of madison square park', 'no-mad'], zipcodes: ['10010', '10016'] },
              'fidi': { aliases: ['financial district', 'downtown', 'lower manhattan'], zipcodes: ['10004', '10005', '10006', '10038'] },
              'midtown': { aliases: ['midtown manhattan', 'central manhattan'], zipcodes: ['10019', '10020', '10036'] },
              'upper east side': { aliases: ['ues', 'upper east'], zipcodes: ['10021', '10028', '10075'] },
              'upper west side': { aliases: ['uws', 'upper west'], zipcodes: ['10023', '10024', '10025'] },
              'chelsea': { aliases: ['west chelsea'], zipcodes: ['10001', '10011'] },
              'west village': { aliases: ['greenwich village', 'village'], zipcodes: ['10014'] },
              'east village': { aliases: ['alphabet city'], zipcodes: ['10003', '10009'] },
              'harlem': { aliases: ['east harlem', 'spanish harlem'], zipcodes: ['10026', '10027', '10030', '10037'] },
              'washington heights': { aliases: ['wash heights', 'heights'], zipcodes: ['10032', '10033', '10040'] }
            }
          },
          'brooklyn': {
            aliases: ['kings county', 'bk', 'brooklyn borough'],
            neighborhoods: {
              'williamsburg': { aliases: ['wburg'], zipcodes: ['11211', '11206'] },
              'dumbo': { aliases: ['down under manhattan bridge overpass'], zipcodes: ['11201'] },
              'park slope': { zipcodes: ['11215', '11217'] },
              'downtown brooklyn': { aliases: ['dtbk', 'downtown bk'], zipcodes: ['11201', '11217'] },
              'bushwick': { zipcodes: ['11206', '11237'] },
              'bed-stuy': { aliases: ['bedford stuyvesant', 'bedstuy'], zipcodes: ['11216', '11221', '11233'] }
            }
          },
          'queens': {
            aliases: ['queens county', 'queens borough'],
            neighborhoods: {
              'long island city': { aliases: ['lic'], zipcodes: ['11101', '11109'] },
              'astoria': { zipcodes: ['11102', '11103', '11105'] },
              'flushing': { zipcodes: ['11354', '11355'] }
            }
          }
        }
      },
      'miami': {
        aliases: ['miami, fl', 'miami beach', 'south beach', 'miami-dade'],
        neighborhoods: {
          'brickell': { aliases: ['brickell key', 'miami financial district'] },
          'south beach': { aliases: ['sobe', 'south beach miami'] },
          'downtown miami': { aliases: ['downtown'] },
          'wynwood': { aliases: ['wynwood arts district'] }
        }
      },
      'chicago': {
        aliases: ['chicago, il', 'windy city', 'chi-town'],
        neighborhoods: {
          'the loop': { aliases: ['downtown chicago', 'loop'] },
          'river north': { aliases: ['rivernorth'] },
          'west loop': { aliases: ['westloop', 'fulton market'] }
        }
      },
      'los angeles': {
        aliases: ['la', 'los angeles, ca', 'l.a.'],
        neighborhoods: {
          'downtown la': { aliases: ['dtla', 'downtown los angeles'] },
          'santa monica': { aliases: ['sm'] },
          'hollywood': { aliases: ['hollywood hills'] }
        }
      },
      'san francisco': {
        aliases: ['sf', 'san francisco, ca', 's.f.', 'bay area'],
        neighborhoods: {
          'soma': { aliases: ['south of market', 'south market'] },
          'financial district': { aliases: ['fidi', 'downtown sf'] },
          'mission': { aliases: ['mission district'] }
        }
      }
    },
    
    // Building type classifications
    building_types: {
      'residential': {
        synonyms: ['housing', 'residential building', 'residential development'],
        subcategories: {
          'luxury residential': { 
            synonyms: ['high-end residential', 'luxury housing', 'luxury condo', 'luxury apartment', 'ultra-luxury', 'premium residential'],
            indicators: ['luxury', 'high-end', 'premium', 'ultra', 'exclusive']
          },
          'multifamily': { 
            synonyms: ['multi-family', 'apartment building', 'apartment complex', 'rental'],
            types: ['apartment', 'rental apartment', 'condominium', 'condo']
          },
          'affordable housing': {
            synonyms: ['low income housing', 'subsidized housing', 'workforce housing', 'affordable'],
            indicators: ['affordable', 'low-income', 'workforce', 'subsidized']
          },
          'student housing': {
            synonyms: ['dormitory', 'student apartments', 'university housing']
          },
          'senior housing': {
            synonyms: ['assisted living', 'retirement community', 'senior apartments']
          }
        }
      },
      'office': {
        synonyms: ['commercial office', 'office building', 'corporate', 'workplace'],
        subcategories: {
          'class a office': { synonyms: ['class-a', 'grade a', 'premium office', 'trophy office'] },
          'co-working': { synonyms: ['coworking', 'shared workspace', 'flexible office'] },
          'creative office': { synonyms: ['tech office', 'startup office'] }
        }
      },
      'hotel': {
        synonyms: ['hospitality', 'lodging', 'accommodation'],
        subcategories: {
          'luxury hotel': { synonyms: ['5-star', 'upscale hotel', 'premium hotel', 'boutique hotel'] },
          'extended stay': { synonyms: ['extended-stay', 'aparthotel', 'serviced apartments'] }
        }
      },
      'retail': {
        synonyms: ['shopping', 'commercial retail', 'stores'],
        subcategories: {
          'ground floor retail': { synonyms: ['street retail', 'ground level retail', 'integrated retail'] },
          'shopping center': { synonyms: ['mall', 'shopping mall', 'retail center'] }
        }
      },
      'mixed-use': {
        synonyms: ['mixed use', 'multi-use', 'mixed-use development'],
        indicators: ['mixed', 'multi-use', 'combined use']
      }
    },
    
    // Construction methods and terminology
    construction_terms: {
      'value engineering': {
        synonyms: ['cost reduction', 'budget optimization', 'cost savings', 've', 'cost engineering'],
        definition: 'Systematic method to improve project value by examining function vs cost'
      },
      'fast-track': {
        synonyms: ['fast track', 'accelerated schedule', 'compressed timeline', 'expedited construction', 'rapid delivery'],
        definition: 'Overlapping design and construction phases to reduce overall schedule'
      },
      'design-build': {
        synonyms: ['design build', 'integrated delivery', 'single source', 'db', 'design-builder'],
        definition: 'Project delivery method combining design and construction under one contract'
      },
      'ground-up': {
        synonyms: ['ground up', 'new construction', 'new build', 'greenfield', 'new development'],
        definition: 'Construction on vacant land starting from foundation'
      },
      'adaptive reuse': {
        synonyms: ['adaptive re-use', 'building conversion', 'repurposing', 'rehabilitation'],
        definition: 'Converting existing building to new use'
      },
      'core and shell': {
        synonyms: ['core & shell', 'base building', 'cold shell', 'warm shell'],
        definition: 'Building structure and systems without interior fit-out'
      },
      'tenant improvement': {
        synonyms: ['ti', 'tenant fit-out', 'interior build-out', 'fit out', 'build-out'],
        definition: 'Interior construction customized for tenant needs'
      }
    },
    
    // Cost metrics by building type
    cost_metrics: {
      'hotel': ['cost per key', 'cost per room', 'cost/key'],
      'healthcare': ['cost per bed', 'cost/bed'],
      'residential': ['cost per unit', 'cost/unit', 'cost per sf', 'cost/sf'],
      'office': ['cost per sf', 'cost/sf', 'cost per rsf'],
      'parking': ['cost per space', 'cost/space', 'cost per stall']
    }
  };
  
  /**
   * Generate embedding for text using OpenAI API
   */
  static async getEmbedding(text, apiKey) {
    // Check cache first
    const cacheKey = text.toLowerCase().trim();
    if (this.embeddingCache.has(cacheKey)) {
      console.log('🎯 Using cached embedding for:', text.substring(0, 50));
      return this.embeddingCache.get(cacheKey);
    }
    
    try {
      console.log('🧠 Generating embedding via backend for:', text.substring(0, 50));
      
      // Call backend API for embeddings (uses OpenAI with valid key)
      const apiBase = process.env.VUE_APP_ENV_API_BASE || 'http://167.172.25.255:3000/api/v1/';
      const response = await axios.post(
        `${apiBase}embedding_service/generate`,
        {
          text: text
        },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      const embedding = response.data.embedding;
      
      // Cache the result
      this.embeddingCache.set(cacheKey, embedding);
      
      return embedding;
    } catch (error) {
      console.error('❌ Embedding generation failed:', error.response?.data || error.message);
      console.log('⚠️ Falling back to text-based search without embeddings');
      return null;
    }
  }
  
  /**
   * Calculate cosine similarity between two vectors
   */
  static cosineSimilarity(vec1, vec2) {
    if (!vec1 || !vec2 || vec1.length !== vec2.length) {
      return 0;
    }
    
    let dotProduct = 0;
    let norm1 = 0;
    let norm2 = 0;
    
    for (let i = 0; i < vec1.length; i++) {
      dotProduct += vec1[i] * vec2[i];
      norm1 += vec1[i] * vec1[i];
      norm2 += vec2[i] * vec2[i];
    }
    
    const magnitude = Math.sqrt(norm1) * Math.sqrt(norm2);
    return magnitude === 0 ? 0 : dotProduct / magnitude;
  }
  
  /**
   * Expand query with synonyms and related terms from ontology
   */
  static expandQueryTerms(query) {
    const queryLower = query.toLowerCase();
    const expandedTerms = new Set([query]); // Include original
    const extractedConcepts = {
      locations: [],
      buildingTypes: [],
      heightClass: null,
      constructionTerms: [],
      costMetrics: []
    };
    
    // Expand building height terms - check most specific first (super tall before tall)
    // 🔥 FIX: Check for "super tall" FIRST with exact phrase match to avoid "tall" substring matching
    const heightClasses = [
      ['super tall', this.CONSTRUCTION_ONTOLOGY.building_height['super tall']],
      ['tall', this.CONSTRUCTION_ONTOLOGY.building_height['tall']],
      ['high-rise', this.CONSTRUCTION_ONTOLOGY.building_height['high-rise']],
      ['mid-rise', this.CONSTRUCTION_ONTOLOGY.building_height['mid-rise']],
      ['low-rise', this.CONSTRUCTION_ONTOLOGY.building_height['low-rise']]
    ];
    
    for (const [heightClass, config] of heightClasses) {
      if (!config) continue;
      
      // Use word boundary check to avoid "tall" matching inside "super tall"
      const exactMatch = queryLower.includes(heightClass);
      const synonymMatch = config.synonyms.some(syn => queryLower.includes(syn));
      
      if (exactMatch || synonymMatch) {
        expandedTerms.add(heightClass);
        config.synonyms.forEach(syn => expandedTerms.add(syn));
        extractedConcepts.heightClass = {
          class: heightClass,
          min_height: config.min_height,
          max_height: config.max_height
        };
        console.log(`📏 Detected height class: ${heightClass} (${config.min_height}${config.max_height ? '-' + config.max_height : '+'}ft)`);
        break; // 🔥 FIX: Stop after first (most specific) match!
      }
    }
    
    // Expand location terms with neighborhood hierarchy
    const expandLocation = (locationName, locationData, parent = null) => {
      const matches = [locationName, ...(locationData.aliases || [])].some(term => 
        queryLower.includes(term.toLowerCase())
      );
      
      if (matches) {
        expandedTerms.add(locationName);
        (locationData.aliases || []).forEach(alias => expandedTerms.add(alias));
        extractedConcepts.locations.push({
          name: locationName,
          parent: parent,
          zipcodes: locationData.zipcodes,
          neighborhoods: locationData.neighborhoods
        });
        console.log(`📍 Detected location: ${locationName}${parent ? ` (in ${parent})` : ''}`);
      }
      
      // Check children/neighborhoods
      if (locationData.children) {
        for (const [childName, childData] of Object.entries(locationData.children)) {
          expandLocation(childName, childData, locationName);
        }
      }
      if (locationData.neighborhoods) {
        for (const [neighborhoodName, neighborhoodData] of Object.entries(locationData.neighborhoods)) {
          expandLocation(neighborhoodName, neighborhoodData, locationName);
        }
      }
    };
    
    for (const [cityName, cityData] of Object.entries(this.CONSTRUCTION_ONTOLOGY.locations)) {
      expandLocation(cityName, cityData);
    }
    
    // Expand building type terms
    const expandBuildingType = (typeName, typeData) => {
      const matches = [typeName, ...(typeData.synonyms || [])].some(term =>
        queryLower.includes(term.toLowerCase())
      );
      
      if (matches) {
        expandedTerms.add(typeName);
        (typeData.synonyms || []).forEach(syn => expandedTerms.add(syn));
        extractedConcepts.buildingTypes.push(typeName);
        console.log(`🏢 Detected building type: ${typeName}`);
      }
      
      // Check subcategories
      if (typeData.subcategories) {
        for (const [subName, subData] of Object.entries(typeData.subcategories)) {
          const subMatches = [subName, ...(subData.synonyms || [])].some(term =>
            queryLower.includes(term.toLowerCase())
          );
          
          if (subMatches) {
            expandedTerms.add(subName);
            (subData.synonyms || []).forEach(syn => expandedTerms.add(syn));
            extractedConcepts.buildingTypes.push(subName);
            console.log(`🏢 Detected building subtype: ${subName}`);
          }
        }
      }
    };
    
    for (const [typeName, typeData] of Object.entries(this.CONSTRUCTION_ONTOLOGY.building_types)) {
      expandBuildingType(typeName, typeData);
    }
    
    // Expand construction terminology
    for (const [term, termData] of Object.entries(this.CONSTRUCTION_ONTOLOGY.construction_terms)) {
      if (queryLower.includes(term) || termData.synonyms.some(syn => queryLower.includes(syn))) {
        expandedTerms.add(term);
        termData.synonyms.forEach(syn => expandedTerms.add(syn));
        extractedConcepts.constructionTerms.push({
          term: term,
          definition: termData.definition
        });
        console.log(`🔧 Detected construction term: ${term}`);
      }
    }
    
    // Detect cost metrics
    for (const [buildingType, metrics] of Object.entries(this.CONSTRUCTION_ONTOLOGY.cost_metrics)) {
      for (const metric of metrics) {
        if (queryLower.includes(metric)) {
          extractedConcepts.costMetrics.push({ buildingType, metric });
          console.log(`💰 Detected cost metric: ${metric} (${buildingType})`);
        }
      }
    }
    
    const result = {
      expandedTerms: Array.from(expandedTerms),
      concepts: extractedConcepts
    };
    
    console.log('🔍 Query expansion result:', {
      original: query,
      expandedCount: result.expandedTerms.length,
      locations: extractedConcepts.locations.length,
      buildingTypes: extractedConcepts.buildingTypes.length,
      heightClass: extractedConcepts.heightClass?.class
    });
    
    return result;
  }
  
  /**
   * Create searchable text for a project (for embedding generation)
   */
  static createProjectSearchText(project) {
    const parts = [];
    
    // Project name
    if (project['Project Name'] || project.name) {
      parts.push(project['Project Name'] || project.name);
    }
    
    // Location
    if (project['City'] || project.city) {
      parts.push(project['City'] || project.city);
    }
    if (project['State'] || project.state) {
      parts.push(project['State'] || project.state);
    }
    if (project['Project Street Address'] || project.address_line_1) {
      parts.push(project['Project Street Address'] || project.address_line_1);
    }
    
    // Building types
    if (project['Use Type 1']) parts.push(project['Use Type 1']);
    if (project['Use Type 2']) parts.push(project['Use Type 2']);
    if (project['Asset Category']) parts.push(project['Asset Category']);
    if (project.primary_building_use_type) parts.push(project.primary_building_use_type);
    if (project.building_use_types_full) {
      parts.push(...project.building_use_types_full);
    }
    
    // Construction type
    if (project['Development Type']) parts.push(project['Development Type']);
    if (project.project_construction_types_s) {
      parts.push(...project.project_construction_types_s);
    }
    
    // Size indicators (convert to text descriptions)
    if (project.total_gsf || project['Total Building GSF']) {
      const gsf = project.total_gsf || project['Total Building GSF'];
      if (gsf > 500000) parts.push('large scale development');
      if (gsf > 1000000) parts.push('mega project');
    }
    
    if (project.total_height || project['Total Height (ft)']) {
      const height = project.total_height || project['Total Height (ft)'];
      if (height >= 984) parts.push('super tall tower supertall');
      else if (height >= 600) parts.push('tall tower');
      else if (height >= 75) parts.push('high-rise tower');
      else if (height >= 35) parts.push('mid-rise building');
    }
    
    // Key players
    if (project['Primary Client']) parts.push(project['Primary Client']);
    if (project['Developer Sponsor 1']) parts.push(project['Developer Sponsor 1']);
    if (project.primary_project_developer) parts.push(project.primary_project_developer);
    
    return parts.filter(Boolean).join(' ');
  }
  
  /**
   * Apply semantic filters based on extracted concepts
   */
  static applyConceptFilters(projects, concepts) {
    let filtered = projects;
    
    // Apply height filters
    if (concepts.heightClass) {
      const { min_height, max_height } = concepts.heightClass;
      filtered = filtered.filter(p => {
        const height = p.total_height || p['Total Height (ft)'] || 0;
        const meetsMin = !min_height || height >= min_height;
        const meetsMax = !max_height || height <= max_height;
        return meetsMin && meetsMax;
      });
      console.log(`📏 Filtered by height (${min_height}-${max_height}ft): ${filtered.length} projects`);
    }
    
    // Apply location filters (including neighborhood zipcodes and borough detection)
    if (concepts.locations.length > 0) {
      filtered = filtered.filter(p => {
        const projectCity = (p.city || p['City'] || '').toLowerCase();
        const projectState = (p.state || p['State'] || '').toLowerCase();
        const projectZip = (p.zipcode || p['Zip Code'] || '').toString();
        const zipNum = parseInt(projectZip.substring(0, 5));
        
        // Determine NYC borough from zip code
        let projectBorough = null;
        if (zipNum >= 10001 && zipNum <= 10282) projectBorough = 'manhattan';
        else if (zipNum >= 11201 && zipNum <= 11256) projectBorough = 'brooklyn';
        else if ((zipNum >= 11004 && zipNum <= 11109) || (zipNum >= 11351 && zipNum <= 11697)) projectBorough = 'queens';
        else if (zipNum >= 10451 && zipNum <= 10475) projectBorough = 'bronx';
        else if (zipNum >= 10301 && zipNum <= 10314) projectBorough = 'staten island';
        
        return concepts.locations.some(loc => {
          const locName = loc.name.toLowerCase();
          
          // Direct city match
          if (projectCity.includes(locName)) return true;
          
          // NYC Borough matching via zip code
          const nycBoroughs = ['manhattan', 'brooklyn', 'queens', 'bronx', 'staten island'];
          if (nycBoroughs.includes(locName) && projectBorough === locName) {
            console.log(`📍 ZIP ${projectZip} → ${projectBorough} matches query "${locName}"`);
            return true;
          }
          
          // NYC general match - any NYC borough qualifies
          if (['new york city', 'nyc', 'new york'].includes(locName) && projectBorough) {
            return true;
          }
          
          // Also match if city is "New York" and searching for any NYC location
          if (projectCity.includes('new york') && (nycBoroughs.includes(locName) || ['new york city', 'nyc', 'new york'].includes(locName))) {
            return true;
          }
          
          // State match for NY
          if (projectState === 'ny' && ['new york', 'nyc', 'new york city'].includes(locName)) {
            return true;
          }
          
          // Neighborhood zipcode match
          if (loc.zipcodes && loc.zipcodes.includes(projectZip)) return true;
          
          // Check if location has neighborhoods with matching zipcodes
          if (loc.neighborhoods) {
            for (const [, nhData] of Object.entries(loc.neighborhoods)) {
              if (nhData.zipcodes && nhData.zipcodes.includes(projectZip)) return true;
            }
          }
          
          return false;
        });
      });
      console.log(`📍 Filtered by location: ${filtered.length} projects`);
    }
    
    return filtered;
  }
}

export default SemanticSearchService;

